import {AbstractSystemPermission} from "./AbstractSystemPermission";
import {ISubmitDataOverride, TSystemPermissionStatus} from "./index";
import {Organization} from "./Organization";
import {IObjectData, IOverride, ISystem, ISystemPermission, ISystemRole} from "../../../index";
import {_} from 'lodash';
import {ObjectTypeId} from "../../../constants";
import {getSyncFieldValue} from "../../../util/misc";
import {nsOverride} from "../../override/nsOverride";
import pubsub from "../../../pubsub";
import getBasicOverride = nsOverride.getBasicOverride;
import OverrideValue = nsOverride.OverrideValue;

export class Automatic extends AbstractSystemPermission {
    protected readonly isOverrideable: boolean = nsOverride.showOverride();

    constructor(org: Organization, user: IObjectData | null, sp: ISystemPermission, doInit: boolean = true) {
        super(org, user, sp);
        if (doInit) {
            this.init();
        }
    }

    protected override init(): void {
        super.init();
        this.override(this.getOriginalOverride() ?? null);
        this.isDeleted(!!this.override());
        this.initData();
    }

    protected override initData() {
        super.initData();
        this.managerOf(this.sp.managerOf);
        this.role(this.sp.role);
        pubsub.trigger(this.resetRoleEventName, this.sp.role.id);
    }

    getStatus(): TSystemPermissionStatus {
        const isDeleted = this.isDeleted();
        const hasOverride = !!this.override();
        if (isDeleted && !hasOverride) {
            return 'deleted';
        }
        const hasOriginalOverride = !!this.getOriginalOverride();
        const isPropertiesChanged = this.isPropertiesChanged();
        if (!hasOverride && hasOriginalOverride && !isPropertiesChanged) {
            return 'restored';
        }
        if (isPropertiesChanged) {
            return 'changed';
        }
        return 'unchanged';
    }

    delete() {
        this.initData();
        this.override(null);
        this.isDeleted(true);
    }

    restore() {
        this.override(null);
        this.isDeleted(false);
        this.initData();
    }

    getSubmitDataOverride(): ISubmitDataOverride | null {
        if (this.getStatus() == 'unchanged') {
            return null;
        }
        return {
            old: this.getOriginalOverride() ?? null,
            new: this.getOverrides(),
            status: this.getStatus(),
        }
    }

    protected getOverrides(): IOverride | null {
        switch (this.getStatus()) {
            case "changed":
            case "deleted":
                return this.getDeleteOverrideData();
            default:
                return null;
        }
    }

    protected getDeleteOverrideData(): IOverride {
        const object = this.sp.role;
        const parent = this.sp.system;
        return this.getOverrideData(object, parent, OverrideValue.Remove + (this.sp.managerOf || ''));
    }

    private getOverrideData(object: ISystemRole, parent: ISystem, value: string): IOverride {
        const target = this.user;
        if (!target) {
            throw new Error('User is required for override data');
        }
        return _.defaults({
            companyid: parent.companyid,
            objectid: object.id,
            objecttypeid: ObjectTypeId.System,
            parentsourceid: parent.id,
            parentsourcename: parent.name,
            sourcename: object.name,
            targetname: getSyncFieldValue('DisplayName', target.syncfields),
            targetobjectid: target.id,
            targetobjectsrc: getSyncFieldValue('SourceId', target.syncfields),
            targetobjecttypeid: target.type.id,
            value: value,
        }, getBasicOverride());
    }

    containsNewOverrides(): boolean {
        switch (this.getStatus()) {
            case "changed":
            case "deleted":
                return true;
            default:
                return false;
        }
    }

    protected getOriginalOverride(): IOverride | undefined {
        const target = this.user;
        if (!target) {
            return undefined;
        }
        return target.overrides?.find(o => {
            return ObjectTypeId.System == o.objecttypeid &&
                this.sp.system?.id == o.parentsourceid &&
                this.sp.role?.id == o.objectid &&
                (this.sp.managerOf ? this.sp.managerOf == o.departmentid : true);
        });
    }

    protected override showRestoreButton(): boolean {
        if (this.getStatus() == 'unchanged' && this.override()) {
            return true;
        }
        return super.showRestoreButton();
    }



}